import { t } from 'i18next';
import { useState } from 'react';
import { Box, Flex } from 'reflexbox';
import { useMediaQuery } from 'usehooks-ts';

import { ArrowLeftIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Breakpoint, Button, Spinner, tokens } from '@treams/treams-ui';

import KeepPreviousAnswersCallout from './KeepPreviousAnswersCallout';
import EvaluationSubmissionComponent from './components/EvaluationSubmission';
import EvaluationSubmissionSidebarLayout from './components/layout/EvaluationSubmissionSidebarLayout';
import LayoutSection from './components/layout/LayoutSection';
import { StreamingAIInsightsContextProvider } from './contexts/StreamingAIInsightsContextProvider';
import useDetectPrint from './hooks/useDetectPrint';

export default function EvaluationSubmission(props: {
	evaluationSubmissionID: string;
	onCompleted?: () => void;
	returnUrl?: string;
}) {
	const isPrinting = useDetectPrint();
	const [formKey, setFormKey] = useState(0);
	const isMobile = useMediaQuery(`(max-width: ${Breakpoint.Medium})`, {
		initializeWithValue: true,
	});
	const [isSidebarOpen, setIsSidebarOpen] = useState(!isPrinting && !isMobile);

	const { data, refetch } = graphql.useEvaluationSubmissionQuery({
		variables: {
			id: props.evaluationSubmissionID,
		},
		onCompleted: () => {
			props.onCompleted?.();
		},
	});

	if (!data) {
		return <Spinner centered my={4} />;
	}

	const handlePreviousAnswersDecision = async () => {
		await refetch();
		setFormKey((key) => key + 1);
	};

	return (
		<EvaluationSubmissionSidebarLayout
			id={props.evaluationSubmissionID}
			user={data.evaluationSubmission.employee.user as graphql.User}
			onSidebarOpen={(newValue) => setIsSidebarOpen(newValue)}
			isSideBarOpen={isSidebarOpen}
		>
			{!isPrinting && data.evaluationSubmission.viewerCanDiscardAnswersFromPredecessor && (
				<KeepPreviousAnswersCallout
					id={props.evaluationSubmissionID}
					onCompleted={handlePreviousAnswersDecision}
				/>
			)}
			<Box py={4}>
				{!isPrinting && (
					<LayoutSection>
						<Flex mb={`${tokens.gap.section}px`}>
							<Button
								icon={<ArrowLeftIcon />}
								link={props.returnUrl}
								title={t('goals.show.toolbar.back')}
								type="tertiary"
								ml="-12px"
							/>
						</Flex>
					</LayoutSection>
				)}

				<StreamingAIInsightsContextProvider
					key={data.evaluationSubmission.id}
					submission={data.evaluationSubmission}
				>
					<EvaluationSubmissionComponent
						key={formKey}
						submission={data.evaluationSubmission}
						onRefresh={refetch}
					/>
				</StreamingAIInsightsContextProvider>
			</Box>
		</EvaluationSubmissionSidebarLayout>
	);
}
